import { useLocation, useNavigate } from 'react-router-dom';
import { Heading, Text, Button, Card } from 'frosted-ui';
import { ArrowLeft16, ArrowUpRightFromSquare16 } from '@frosted-ui/icons';
import { Rise } from '../components/animation';
import { useCompanyId } from '../contexts/CompanyContext';
import { isFirstRunState, type FirstRunState } from '../components/Onboarding/firstRun';

const STEPS = [
  {
    title: 'Create a free product on Whop',
    body: 'A free community, signals channel, or guide — something people join without paying. In your Whop dashboard: Products → Create product → set the price to Free.',
  },
  {
    title: 'Point your content at it',
    body: 'Put the free product in your bio and pinned posts. Free is an easy yes, so it grows faster than a paid offer.',
  },
  {
    title: 'Come back for an in-whop test',
    body: 'Once people are joining, start a test "Inside your whop": free members see an upgrade offer, and we find the price that turns the most of them into paying members.',
  },
];

/** "I don't have buyers yet": a test needs traffic, so start with a free tier. */
export default function NoBuyersGuide() {
  const navigate = useNavigate();
  const location = useLocation();
  const companyId = useCompanyId();
  // Back to the chooser keeps first run (its step header and Back button).
  const backState: FirstRunState = { firstRun: isFirstRunState(location.state) };

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
      <div>
        <Button
          variant="ghost"
          color="gray"
          size="2"
          style={{ minHeight: 44 }}
          onClick={() => navigate('/new', { state: backState })}
        >
          <ArrowLeft16 />
          Where buyers find you
        </Button>
      </div>
      <Rise>
        <Heading size="7" style={{ marginBottom: 'var(--space-2)' }}>
          First, give buyers a way in
        </Heading>
        <Text size="3" color="gray">
          A price test compares what people do at checkout, so it needs people reaching your
          checkout. The quickest way to get them is a free tier.
        </Text>
      </Rise>

      <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        {STEPS.map((step, i) => (
          <Rise key={step.title} delay={80 + i * 60}>
            <li>
              <Card size="2">
                <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'flex-start' }}>
                  <span
                    aria-hidden="true"
                    style={{
                      flexShrink: 0,
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'var(--accent-a4)',
                      color: 'var(--accent-11)',
                      fontWeight: 600,
                      fontSize: 14,
                    }}
                  >
                    {i + 1}
                  </span>
                  <div>
                    <Heading size="3" style={{ marginBottom: 'var(--space-1)' }}>{step.title}</Heading>
                    <Text size="2" color="gray">{step.body}</Text>
                  </div>
                </div>
              </Card>
            </li>
          </Rise>
        ))}
      </ol>

      <div className="vara2-button-stack" style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-3)' }}>
        <Button asChild size="3" variant="solid" style={{ flex: '1 1 220px' }}>
          <a href={`https://whop.com/dashboard/${companyId}/`} target="_blank" rel="noopener noreferrer">
            Open my Whop dashboard
            <ArrowUpRightFromSquare16 />
          </a>
        </Button>
        <Button size="3" variant="soft" color="gray" style={{ flex: '1 1 220px' }} onClick={() => navigate('/')}>
          I'll come back later
        </Button>
      </div>
    </div>
  );
}
